import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Row, Col, Button, Form, Input, InputNumber, DatePicker, Select,
  Switch, Space, Tag, App as AntdApp, Empty, Spin, Steps, Result,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  FileText, Variable, ArrowRight, Eye, FileSignature, Search,
  Sparkles, ArrowLeft,
} from 'lucide-react';
import dayjs from 'dayjs';
import { templateService, TemplateField, TemplateListItem } from '@/services/templateService';
import { PdfFullViewer } from '@/components/signing/PdfFullViewer';

// =============================================================
// 1) PICKER — list templates, user chọn 1 cái
// =============================================================
export function TemplatesPickerPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templateService.list(),
  });

  const filtered = templates
    .filter(t => t.isActive)
    .filter(t => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        t.name.toLowerCase().includes(q) ||
        t.code.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q)
      );
    });

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: '0 auto' }}>
      <Card
        title={
          <span>
            <FileText size={18} style={{ verticalAlign: -3, marginRight: 6 }} />
            Tạo văn bản từ mẫu
          </span>
        }
        extra={
          <Input
            prefix={<Search size={14} style={{ color: '#7d8a98' }} />}
            placeholder="Tìm mẫu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
          />
        }
      >
        <p style={{ color: '#7d8a98', marginBottom: 16, fontSize: 13 }}>
          Chọn mẫu văn bản phù hợp, điền thông tin → hệ thống tự sinh PDF và đẩy sang luồng trình ký.
        </p>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : filtered.length === 0 ? (
          <Empty description={
            search ? 'Không tìm thấy mẫu phù hợp'
                   : 'Chưa có mẫu văn bản nào — liên hệ quản trị để tạo mẫu'
          } />
        ) : (
          <Row gutter={[12, 12]}>
            {filtered.map(t => (
              <Col key={t.id} xs={24} sm={12} md={8}>
                <Card
                  hoverable
                  size="small"
                  style={{ height: '100%' }}
                  styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%' } }}
                  onClick={async () => {
                    // Check schema -> nếu có thì paper-fill, không thì generic
                    try {
                      const detail = await templateService.getById(t.id);
                      if (detail.formSchemaJson && detail.formSchemaJson.trim()) {
                        navigate(`/templates/${t.id}/paper-fill`);
                      } else {
                        navigate(`/templates/${t.id}/fill`);
                      }
                    } catch {
                      navigate(`/templates/${t.id}/fill`);
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 6,
                      background: '#e6f4ff', color: '#1976D2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FileText size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{t.name}</div>
                      <code style={{ fontSize: 11, color: '#8a95a5' }}>{t.code}</code>
                    </div>
                  </div>
                  {t.description && (
                    <div style={{
                      fontSize: 12, color: '#5b6875',
                      flex: 1, lineHeight: 1.5, marginBottom: 8,
                    }}>
                      {t.description}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Tag icon={<Variable size={10} />} color="blue" style={{ margin: 0 }}>
                      {t.fieldCount} trường
                    </Tag>
                    {t.useCount > 0 && (
                      <Tag color="green" style={{ margin: 0 }}>{t.useCount} lượt dùng</Tag>
                    )}
                    {t.departmentName && (
                      <Tag style={{ margin: 0 }}>{t.departmentName}</Tag>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card>
    </div>
  );
}

// =============================================================
// 2) FILL FORM — user điền data cho template, render PDF, go to signing
// =============================================================
export function TemplateFillPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();

  const [step, setStep] = useState(0); // 0=fill, 1=preview, 2=ready
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  const { data: template, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templateService.getById(id!),
    enabled: !!id,
  });

  // Cleanup blob URL khi unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handleRender = async (values: any) => {
    if (!template) return;
    setRendering(true);
    try {
      // Convert values → dictionary string
      const data: Record<string, string> = {};
      for (const f of template.fields) {
        if (f.type === 'auto') continue; // backend tự điền
        const v = values[f.name];
        if (v === undefined || v === null || v === '') {
          data[f.name] = '';
          continue;
        }
        if (f.type === 'date') {
          data[f.name] = dayjs(v).format('YYYY-MM-DD');
        } else if (f.type === 'checkbox') {
          data[f.name] = v ? '1' : '0';
        } else if (f.type === 'number') {
          data[f.name] = String(v);
        } else {
          data[f.name] = String(v);
        }
      }

      const blob = await templateService.renderPdf(template.id, data);
      const url = URL.createObjectURL(blob);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfBlob(blob);
      setPdfUrl(url);
      setStep(1);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? 'Render PDF thất bại');
    } finally {
      setRendering(false);
    }
  };

  const handleGoToSigning = () => {
    if (!pdfBlob || !template) return;
    // Convert Blob → File để pass qua location.state
    const fileName = `${template.code}_${dayjs().format('YYYYMMDD_HHmm')}.pdf`;
    const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

    navigate('/signing/new', {
      state: {
        preloadedFile: file,
        suggestedTitle: template.name,
      },
    });
  };

  const handleDownload = () => {
    if (!pdfBlob || !template) return;
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.code}_${dayjs().format('YYYYMMDD_HHmm')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading || !template) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;
  }

  const editableFields = template.fields.filter(f => f.type !== 'auto');
  const autoFields = template.fields.filter(f => f.type === 'auto');

  return (
    <div style={{ padding: 16, maxWidth: 1000, margin: '0 auto' }}>
      <Button
        icon={<ArrowLeft size={14} />}
        type="text"
        onClick={() => navigate('/templates')}
        style={{ marginBottom: 12 }}
      >
        Quay lại danh sách mẫu
      </Button>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 8,
            background: '#e6f4ff', color: '#1976D2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FileText size={24} />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>{template.name}</h2>
            <div style={{ fontSize: 12, color: '#7d8a98' }}>
              <code>{template.code}</code>
              {template.description && <> · {template.description}</>}
            </div>
          </div>
        </div>

        <Steps
          current={step}
          size="small"
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Điền thông tin' },
            { title: 'Xem trước PDF' },
            { title: 'Trình ký' },
          ]}
        />

        {step === 0 && (
          <Form form={form} layout="vertical" onFinish={handleRender}>
            {autoFields.length > 0 && (
              <div style={{
                background: '#f0f7ff', border: '1px solid #d6e4ff',
                borderRadius: 6, padding: 10, marginBottom: 16,
                fontSize: 12, color: '#5b6875',
              }}>
                <Sparkles size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                <strong>Tự động điền:</strong>{' '}
                {autoFields.map(f => f.label).join(', ')}
              </div>
            )}

            {editableFields.length === 0 ? (
              <Empty description="Mẫu này không có trường nào để điền" />
            ) : (
              editableFields.map(f => renderFieldInput(f))
            )}

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={rendering}
                icon={<Eye size={14} />}
              >
                Xem trước PDF
              </Button>
            </Form.Item>
          </Form>
        )}

        {step === 1 && pdfUrl && (
          <div>
            <div style={{
              background: '#f6f7f8', borderRadius: 6,
              padding: 4, marginBottom: 16, height: 600,
            }}>
              {/* V1.0.47: PdfFullViewer cho mobile/iOS */}
              <PdfFullViewer file={pdfUrl} documentName="PDF preview" height="100%" />
            </div>
            <Space>
              <Button onClick={() => setStep(0)} icon={<ArrowLeft size={14} />}>
                Sửa lại
              </Button>
              <Button onClick={handleDownload}>
                Tải PDF về máy
              </Button>
              <Button type="primary" icon={<FileSignature size={14} />} onClick={handleGoToSigning}>
                Đẩy sang Trình ký
              </Button>
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
}

// =============================================================
// Render input theo type
// =============================================================
function renderFieldInput(f: TemplateField) {
  const rules = f.required
    ? [{ required: true, message: `${f.label} là bắt buộc` }]
    : [];

  const item = (() => {
    switch (f.type) {
      case 'textarea':
        return <Input.TextArea rows={3} placeholder={f.label} />;
      case 'number':
        return (
          <InputNumber
            placeholder={f.label}
            style={{ width: '100%' }}
            formatter={(v) => v ? String(v).replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''}
            parser={(v) => v ? Number(String(v).replace(/\./g, '')) as any : null as any}
          />
        );
      case 'date':
        return <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />;
      case 'checkbox':
        return <Switch />;
      case 'select':
        return (
          <Select
            placeholder={`Chọn ${f.label.toLowerCase()}`}
            options={(f.options ?? []).map(o => ({ value: o, label: o }))}
            allowClear
          />
        );
      default:
        return <Input placeholder={f.label} maxLength={500} />;
    }
  })();

  return (
    <Form.Item
      key={f.name}
      label={f.label}
      name={f.name}
      rules={rules}
      valuePropName={f.type === 'checkbox' ? 'checked' : 'value'}
      extra={<code style={{ fontSize: 10, color: '#bfbfbf' }}>{`{{${f.name}}}`}</code>}
    >
      {item}
    </Form.Item>
  );
}
